/* eslint-disable react-refresh/only-export-components */
/**
 * Shared 9:16 video card used by VideoFAQ and CustomControllersPage.
 * Supports YouTube URLs and local /public MP4 files.
 */
import React, { useState } from 'react';
import { Play, Volume2, VolumeX } from '../lib/icons';

export type VideoItem = { id: string; title: string; sub: string; src: string | null };

export const isYouTube = (src: string) =>
  src.includes('youtube.com') || src.includes('youtu.be');

export const getYouTubeEmbedUrl = (src: string, muted: boolean) => {
  const match = src.match(/(?:shorts\/|embed\/|watch\?v=|youtu\.be\/)([a-zA-Z0-9_-]{11})/);
  const id = match?.[1];
  if (!id) return src;
  const muteParam = muted ? '&mute=1' : '&mute=0';
  return `https://www.youtube.com/embed/${id}?autoplay=1${muteParam}&loop=1&playlist=${id}&controls=0&playsinline=1&rel=0`;
};

export const VideoCard = ({
  vid,
  accentColor,
  isActive,
  isMuted,
  onMuteToggle,
  onEnded,
  onActivate,
}: {
  vid: VideoItem;
  accentColor: string;
  isActive: boolean;
  isMuted: boolean;
  onMuteToggle: () => void;
  onEnded: () => void;
  onActivate: () => void;
}) => {
  const [ytMuted, setYtMuted] = useState(true);
  const isYT = vid.src ? isYouTube(vid.src) : false;
  const isMp4Active = isActive && !isYT;

  return (
    <div
      className={`relative aspect-[9/16] rounded-2xl overflow-hidden bg-gray-900 transition-all duration-300 ${
        isMp4Active ? 'border-2 shadow-lg' : 'border border-gray-800'
      }`}
      style={isMp4Active ? { borderColor: accentColor, boxShadow: `0 0 20px ${accentColor}30` } : undefined}
    >
      {vid.src ? (
        isYT ? (
          <>
            <iframe
              key={String(ytMuted)}
              src={getYouTubeEmbedUrl(vid.src, ytMuted)}
              className="w-full h-full"
              allow="autoplay; encrypted-media"
              allowFullScreen
              title={vid.title}
            />
            <button
              onClick={() => setYtMuted(m => !m)}
              className="absolute top-2 right-2 z-10 w-8 h-8 rounded-full bg-black/60 backdrop-blur-sm flex items-center justify-center hover:bg-black/80 transition-colors"
            >
              {ytMuted ? <VolumeX className="h-4 w-4 text-white" /> : <Volume2 className="h-4 w-4 text-white" />}
            </button>
          </>
        ) : isMp4Active ? (
          <>
            <video
              key="active"
              src={vid.src}
              autoPlay
              muted={isMuted}
              playsInline
              onEnded={onEnded}
              className="w-full h-full object-cover"
            />
            <button
              onClick={onMuteToggle}
              className="absolute top-2 right-2 z-10 w-8 h-8 rounded-full bg-black/60 backdrop-blur-sm flex items-center justify-center hover:bg-black/80 transition-colors"
            >
              {isMuted ? <VolumeX className="h-4 w-4 text-white" /> : <Volume2 className="h-4 w-4 text-white" />}
            </button>
          </>
        ) : (
          <button
            onClick={onActivate}
            className="w-full h-full flex flex-col items-center justify-center p-4 text-center group/play"
          >
            <div
              className="w-14 h-14 rounded-full bg-gray-800 border flex items-center justify-center mb-3 group-hover/play:bg-gray-700 transition-all"
              style={{ borderColor: `${accentColor}50` }}
            >
              <Play className="h-5 w-5 ml-0.5" style={{ color: accentColor }} />
            </div>
            <p className="text-[#B0B0B0] text-xs font-semibold leading-tight">{vid.title}</p>
            <p className="text-xs mt-1.5" style={{ color: accentColor }}>Tap to play</p>
          </button>
        )
      ) : (
        <div className="w-full h-full flex flex-col items-center justify-center p-4 text-center">
          <div className="w-14 h-14 rounded-full bg-gray-800 border border-gray-700 flex items-center justify-center mb-3">
            <Play className="h-5 w-5 text-gray-500 ml-0.5" />
          </div>
          <p className="text-[#B0B0B0] text-xs font-semibold leading-tight">{vid.title}</p>
          <p className="text-gray-600 text-xs mt-1.5">Coming soon</p>
        </div>
      )}

      {/* Label overlay */}
      {!(vid.src && !isYT && !isMp4Active) && (
        <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black via-black/70 to-transparent pt-8 pb-3 px-3 pointer-events-none">
          <p className="text-white text-xs font-bold leading-tight">{vid.title}</p>
          <p className="text-xs mt-0.5" style={{ color: accentColor }}>{vid.sub}</p>
        </div>
      )}
    </div>
  );
};
